/**
 * 标注绘制阶段的 Canvas 预览，依赖框架视口变换及标注离屏渲染器
 * 将异步生成的位图与对应图层几何绑定，保证浏览器绘制时两者一致
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { BaseOperateView, getFramework, useScale } from '@/framework';
import type { ILayerProps } from '@/version-types';
import { renderLayerContentToCanvas } from '../../render/render-to-canvas';

/**
 * 在标注绘制期间挂载预览，并依据编辑器缩放同步展示画面与图层几何
 *
 * @param props - 当前预览图层及框架实例标识
 * @returns 不响应鼠标事件的标注预览
 */
export const MarkupEdgelessPreview: React.FC<{ layer: ILayerProps; identity: string }> = React.memo(
    ({ layer, identity }) => {
        const canvasRef = useRef<HTMLCanvasElement>(null);
        const mountedRef = useRef(false);
        const renderSeqRef = useRef(0);
        const paintedSeqRef = useRef(0);
        const [displayFrame, setDisplayFrame] = useState<{
            layer: ILayerProps;
            canvas?: HTMLCanvasElement;
        }>({ layer });
        const displayLayer = displayFrame.layer;
        const scale = useScale();

        useEffect(() => {
            mountedRef.current = true;
            return () => {
                mountedRef.current = false;
            };
        }, []);

        useEffect(() => {
            const renderSeq = ++renderSeqRef.current;
            const retinaScaling = getFramework(identity).service.edgelessCanvas.retinaScaling;

            renderLayerContentToCanvas(layer, identity, false, retinaScaling)
                .then((layerCanvas) => {
                    if (!mountedRef.current || !canvasRef.current || renderSeq < paintedSeqRef.current) return;
                    paintedSeqRef.current = renderSeq;

                    setDisplayFrame({ layer, canvas: layerCanvas });
                })
                .catch(() => {});
        }, [identity, layer]);

        useLayoutEffect(() => {
            const canvas = canvasRef.current;
            const layerCanvas = displayFrame.canvas;
            if (!canvas || !layerCanvas) return;

            // 画面与对应的位置、尺寸在同一次提交中更新，避免新画面套用上一帧包围盒。
            canvas.width = layerCanvas.width;
            canvas.height = layerCanvas.height;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(layerCanvas, 0, 0);
        }, [displayFrame]);

        return (
            <BaseOperateView layer={displayLayer} style={{ pointerEvents: 'none' }}>
                <div
                    style={{
                        width: displayLayer.width,
                        height: displayLayer.height,
                        transform: `scale(${scale})`,
                        transformOrigin: 'left top',
                    }}
                >
                    <canvas
                        ref={canvasRef}
                        style={{
                            width: displayLayer.width,
                            height: displayLayer.height,
                            display: 'block',
                            opacity: displayLayer.opacity,
                        }}
                    />
                </div>
            </BaseOperateView>
        );
    },
);

MarkupEdgelessPreview.displayName = 'MarkupEdgelessPreview';
